import React, { useState, useEffect, lazy, Suspense } from "react";
import axios from "axios";
import "@fontsource/poppins";
import "../src/App.css";
import MastermindsHeader from "./components/MastermindsHeader/MastermindsHeader";
import Body from "./components/Body/Body";
import Spinner from "react-bootstrap/Spinner";
import AnalyticsAndWidgets from "./components/ScriptTags/widget";

const LOBody = lazy(() => import("./components/LOBody/LOBody"));
const InfluencerBody = lazy(() => import("./components/InfluencerBody/InfluencerBody"));
const Footer = lazy(() => import("./components/FooterNew/index.js"));
const Proprty = lazy(() => import("./components/Property/proprty.js"));
const Highlights = lazy(() => import("./components/Highlights/Highlights"));
const USRealEstate = lazy(() => import("./components/Highlights/USRealEstateData.js"));
const Morganlogos = lazy(() => import("./components/Morganlogos/Morganlogos.js"));
const Whysunwest = lazy(() => import("./components/Whysunwest/Morganscan.js"));
const EventComponent = lazy(() => import("./components/Event/Event.js"));
const Testimonial = lazy(() => import("./components/Testimonials/Testimonials.js"));
const Perspective = lazy(() => import("./components/Perspective/perspective.js"));
const VideoPlayer = lazy(() => import("./components/CreditRepair/videoPlayer.js"));
const NewYear = lazy(() => import("./components/NewYear/newyear.js"));

const MemoizedProprty = React.memo(Proprty);
const MemoizedEventComponent = React.memo(EventComponent);
const MemoizedPerspectiveComponent = React.memo(Perspective);
const MemoizedTestimonial = React.memo(Testimonial);
const MemoizedMorganlogos = React.memo(Morganlogos);
const MemoizedWhysunwest = React.memo(Whysunwest);
const MemoizedFooter = React.memo(Footer);
const MemoizedHighlights = React.memo(Highlights);
const MemoizedUSRealEstate = React.memo(USRealEstate);
const MemoizedVideoPlayer = React.memo(VideoPlayer);
const MemoizedNewYear = React.memo(NewYear);
const MemoizedLOBody = React.memo(LOBody);
const MemoizedInfluencerBody = React.memo(InfluencerBody);

function App({ pageData, aiBrandingData, isLoanOfficerPage = false, loanOfficerData = null, isInfluencerPage = false, influencerData = null }) {
  const { banner, bannerImages, newYear, highlights, property, testimonials, Events, signupform, creditRepair, morganlogos, morganscan, usemorgan } = pageData;
  const normalizedLOData = Array.isArray(loanOfficerData) ? loanOfficerData[0] : loanOfficerData;
  const normalizedInfluencerData = Array.isArray(influencerData) ? influencerData[0] : influencerData;

  // Derive pageType once, use everywhere
  const pageType = isLoanOfficerPage ? 'mlo' : isInfluencerPage ? 'influencer' : 'main';

  const [links, setLinks] = useState(null);
  const [showOtherComponents, setShowOtherComponents] = useState(false);
  const [cdnBackgroundImage, setCdnBackgroundImage] = useState(null);

  useEffect(() => {
    axios.get(process.env.REACT_APP_CDN_LINK + "jsons/ai-branding.json")
      .then(res => setLinks(res.data))
      .catch(() => setLinks(null));
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.body.offsetHeight;
      const scrollPosition = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      const maxScroll = documentHeight - windowHeight;
      const percentage = (scrollPosition / maxScroll) * 100;
      if (percentage > 50) setShowOtherComponents(true);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const renderBanner = () => {
    if (!banner) return null;

    if (pageType === 'mlo' && normalizedLOData) {
      return (
        <Suspense fallback={null}>
          <MemoizedLOBody bannerData={banner} loanOfficerData={normalizedLOData} cdnBackgroundImage={cdnBackgroundImage} fetchpriority="high" />
        </Suspense>
      );
    }

    if (pageType === 'influencer' && normalizedInfluencerData) {
      return (
        <Suspense fallback={null}>
          <MemoizedInfluencerBody bannerData={banner} influencerData={normalizedInfluencerData} fetchpriority="high" />
        </Suspense>
      );
    }

    return (
      <Body bannerData={banner} bannerImages={bannerImages} formData={signupform} fetchpriority="high" isLoanOfficerPage={false} />
    );
  };

  return (
    <div className={`bodyContainer ${pageType === 'mlo' ? 'lo-page' : pageType === 'influencer' ? 'influencer-page' : 'main-page'}`}>
      <AnalyticsAndWidgets />
      <MastermindsHeader pageType={pageType} fetchpriority="high" />
      {renderBanner()}

      <Suspense fallback={<Spinner animation="border" role="status" />}>
        <MemoizedNewYear newYearData={newYear} />
        <MemoizedVideoPlayer creditRepairData={creditRepair} />

        {highlights && (
          <Suspense fallback="Loading highlights data...">
            <MemoizedHighlights highlightsData={highlights} />
          </Suspense>
        )}

        {showOtherComponents && (
          <>
            {highlights && (
              <Suspense fallback="Loading highlights data...">
                <MemoizedUSRealEstate highlightsData={highlights} />
              </Suspense>
            )}
            {property && (
              <Suspense fallback="Loading property data...">
                <MemoizedProprty propertyData={property} links={links} />
              </Suspense>
            )}
            <Suspense fallback="Loading events data...">
              <MemoizedEventComponent EventsData={Events} links={links} />
            </Suspense>
            <Suspense fallback="Loading perspective data...">
              <MemoizedPerspectiveComponent aiBrandingData={aiBrandingData} links={links} />
            </Suspense>
            <Suspense fallback="Loading testimonial data...">
              <MemoizedTestimonial testimonialData={testimonials} aiBrandingData={aiBrandingData} links={links} />
            </Suspense>
            <Suspense fallback="Loading Morganlogos data...">
              <MemoizedMorganlogos
                morganlogosData={morganlogos}
                aiBrandingData={aiBrandingData}
                links={links}
                isLoanOfficerPage={isLoanOfficerPage}
                loanOfficerData={normalizedLOData}
                isInfluencerPage={isInfluencerPage}
                influencerData={normalizedInfluencerData}
              />
            </Suspense>
            <Suspense fallback="Loading Whysunwest data...">
              <MemoizedWhysunwest
                morganscanData={morganscan}
                usemorganData={usemorgan}
                aiBrandingData={aiBrandingData}
                links={links}
                isLoanOfficerPage={isLoanOfficerPage}
                loanOfficerData={normalizedLOData}
                isInfluencerPage={isInfluencerPage}
                influencerData={normalizedInfluencerData}
              />
            </Suspense>
            <Suspense fallback="Loading footer data...">
              <MemoizedFooter />
            </Suspense>
          </>
        )}
      </Suspense>
    </div>
  );
}

export default App;
